import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { listEntities, generateFilter } from '@stackwright-pro/cli-data-explorer';
import { jsonCoerce } from '../coerce.js';

/**
 * Register data-explorer MCP tools.
 * Tools for discovering API entities and generating endpoint filters.
 */
export function registerDataExplorerTools(server: McpServer): void {
  // Tool: List available entities
  server.tool(
    'stackwright_pro_list_entities',
    'List all available API entities from OpenAPI specs or generated Zod schemas. ' +
      'Use this to discover what entities are available before generating endpoint filters. ' +
      'Returns entity names, endpoints, and field counts. ' +
      'Part of the Pro Otter Raft for building API-integrated Stackwright applications.',
    {
      specPath: z.string().optional().describe('Path to OpenAPI spec file (YAML or JSON)'),
      projectRoot: z
        .string()
        .optional()
        .describe('Project root directory (auto-detected if omitted)'),
    },
    async ({ specPath, projectRoot }) => {
      const result = listEntities({
        ...(specPath !== undefined && { specPath }),
        ...(projectRoot !== undefined && { projectRoot }),
      });

      if (!result.success) {
        return {
          content: [
            {
              type: 'text' as const,
              text: `Error discovering entities: ${(result.errors ?? []).join(', ')}`,
            },
          ],
          isError: true,
        };
      }

      const lines: string[] = [`📦 Found ${result.entities.length} API Entities:\n`];

      for (const entity of result.entities) {
        lines.push(`📦 ${entity.name} (${entity.slug})`);
        lines.push(`   Endpoint: ${entity.endpoint}`);
        lines.push(`   Fields: ${entity.fieldCount}`);
        if (entity.fields.length > 0) {
          const fieldTypes = entity.fields
            .slice(0, 3)
            .map((f) => `${f.name}:${f.type}`)
            .join(', ');
          lines.push(`   Types: ${fieldTypes}${entity.fields.length > 3 ? '...' : ''}`);
        }
        lines.push('');
      }

      lines.push(
        `\n💡 Use stackwright_pro_generate_filter with selected entity slugs to create endpoint filters.`
      );

      return {
        content: [
          {
            type: 'text' as const,
            text: lines.join('\n'),
          },
        ],
      };
    }
  );

  // Tool: Generate endpoint filter
  server.tool(
    'stackwright_pro_generate_filter',
    'Generate endpoint filter configuration from selected entities. ' +
      'Creates include/exclude patterns for stackwright.yml OpenAPI integration. ' +
      'Use this after stackwright_pro_list_entities to select which API endpoints the application needs. ' +
      'Only selected endpoints will generate client code, reducing bundle size and improving security.',
    {
      selectedEntities: jsonCoerce(z.array(z.string())).describe(
        'Entity slugs to include (e.g., ["equipment", "supplies"])'
      ),
      excludePatterns: jsonCoerce(z.array(z.string()).optional()).describe(
        'Glob patterns to exclude (e.g., ["/admin/**", "/reports/**"])'
      ),

      projectRoot: z.string().optional().describe('Project root directory'),
    },
    async ({ selectedEntities, excludePatterns, projectRoot }) => {
      const result = generateFilter({
        selectedEntities,
        ...(excludePatterns !== undefined && { excludePatterns }),
        projectRoot: projectRoot ?? process.cwd(),
      });

      if (!result.success) {
        return {
          content: [
            {
              type: 'text' as const,
              text: `Error generating filter: ${result.warnings?.join(', ')}`,
            },
          ],
          isError: true,
        };
      }

      const lines: string[] = ['🔐 Generated Endpoint Filter:\n'];
      lines.push('```yaml');
      lines.push('integrations:');
      lines.push('  - type: openapi');
      lines.push('    endpoints:');

      if (result.filter.include && result.filter.include.length > 0) {
        lines.push('      include:');
        for (const path of result.filter.include) {
          lines.push(`        - ${path}`);
        }
      }

      if (result.filter.exclude && result.filter.exclude.length > 0) {
        lines.push('      exclude:');
        for (const pattern of result.filter.exclude) {
          lines.push(`        - ${pattern}`);
        }
      }

      lines.push('```');

      if (result.warnings && result.warnings.length > 0) {
        lines.push('\n⚠️ Warnings:');
        for (const warning of result.warnings) {
          lines.push(`  ${warning}`);
        }
      }

      return {
        content: [
          {
            type: 'text' as const,
            text: lines.join('\n'),
          },
        ],
      };
    }
  );
}
